import { AccountStatus, AccountType, AccountModel, IAccountData, AccountQueryOptions, DuplicateAccountsModel } from '@truckdown/accounts';
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, nextTick } from 'vue';

const resultsPerPage = 10;

interface IData {
    isLoading: Ref<boolean>;
    duplicateAccountsQuery: Ref<DuplicateAccountsModel>;
    duplicateAccounts: Ref<AccountModel[]>;
    duplicateAccountsCount: Ref<number>;
    lastNumber: Ref<number | undefined>;
    pageCount: Ref<number>;
    resetList: Ref<boolean>;
    showDuplicateAccountDropdown: Ref<number>;
}

interface IPageKey {
    lastNumber?: number
}

const getInitialData = function (props: any) {
    return {
        isLoading: ref<boolean>(false),
        duplicateAccountsQuery: ref<DuplicateAccountsModel>(props.query),
        duplicateAccountsCount: ref<number>(props.count || 0),
        duplicateAccounts: ref<AccountModel[]>(props?.duplicateAccounts || []),
        lastNumber: ref<number | undefined>(props.query.lastNumber),
        pageCount: ref<number>(resultsPerPage),
        resetList: ref<boolean>(false),
        showDuplicateAccountDropdown: ref<number>(-1),

    } as IData;
};

const getComputed = function (data: IData) {
    return {
        lastPagerObject: computed(() => {
            if (data.duplicateAccounts.value && data.duplicateAccounts.value.length > 0) {
                let last = (data.duplicateAccounts.value)[data.duplicateAccounts.value.length - 1];
                return {
                    lastNumber: last.ain
                }
            }
        })
    };
};

const getMethods = function (data: IData, props: any) {
    return {
        updateList: async function (updateArgs?: IPageKey) {
            data.showDuplicateAccountDropdown.value = -1;
            await loadDuplicateAccounts(data, updateArgs);
        },
        getStatus: function (status: AccountStatus) {
            return AccountStatus[status];
        },
        getStatusClass: function (status: AccountStatus) {
            if (status === AccountStatus.Active) {
                return 'td-badge success'
            } else if (status === AccountStatus.DNA || status === AccountStatus.Purge) {
                return 'td-badge danger'
            } else {
                return 'td-badge primary'
            }
        },
        getType: function (type: AccountType) {
            return AccountType[type];
        },
        getAddress: function (account: AccountModel) {
            if (account.info?.address?.city) {
                return account.info.address.city + ", " + account.info.address.regionCode;
            }
            return "";
        },
        toggleDuplicateAccountDropdown: function (index: number) {
            if (data.showDuplicateAccountDropdown.value == index) {
                data.showDuplicateAccountDropdown.value = -1;
            } else {
                data.showDuplicateAccountDropdown.value = index;
            }
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC) {
                return new Date(dateUTC).toLocaleString();
            } else
                return undefined;
        },
        goToAccount: function (account: AccountModel) {
            window.location.href = `/accounts/admin/${encodeURIComponent(account.ain)}`;
        },
        formatPhoneString: function (phone: string) {
            if (phone?.length == 10) {
                phone = phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
            }
            return phone;
        }
    };
}

const triggerResetList = async function (data: IData, ev?: Event) {
    data.resetList.value = true;
    nextTick(() => {
        data.resetList.value = false;
    });
}

const loadDuplicateAccounts = async function (data: IData, page?: IPageKey) {
    if (!data.isLoading.value && data.duplicateAccountsQuery.value) {
        data.isLoading.value = true;
        try {
            if (page) {
                data.duplicateAccountsQuery.value.lastNumber = page.lastNumber;
            } else {
                data.duplicateAccountsQuery.value.lastNumber = undefined;
            }

            const accountData = await context.getService<IAccountData>('IAccountData');
            const result = await accountData.getDuplicateAccounts(data.duplicateAccountsQuery.value);

            if (result.ok && result.data) {
                data.duplicateAccounts.value = result.data;
                if (!page) {
                    await triggerResetList(data);
                }
            }
            if (!result.ok) {
                console.error("Error querying duplicate accounts.");
            }

        } catch (err) {
            console.error("Error querying duplicate accounts.");
        } finally {
            data.isLoading.value = false;
        }
    }
};


export default defineComponent({
    name: "DuplicateAccounts",
    props: {
        duplicateAccounts: Array as () => AccountModel[],
        count: Number,
        query: Object as () => DuplicateAccountsModel
    },
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(data, props);
        let computed = getComputed(data);

        return {
            ...data,
            ...methods,
            ...computed
        }
    }
})